/**
 * RenderServerJobs — unified audit-log of EVERY job that runs on the render
 * server, across all tools (Naamtitels, Narrowcasting, Ondertitels/SRT,
 * Video-QA, Ritmo). Filterable by job-type, status, user, machine and date.
 * Data via /Api/render-server-jobs(/stats). Reads ?worker_id / ?job_category
 * from the URL so the "Logs bekijken" deeplink from Render servers pre-filters.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Chip, IconButton,
  Drawer, CircularProgress, Pagination,
} from '@mui/material';
import {
  FilterList as FilterIcon, Refresh as RefreshIcon, Close as CloseIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, SectionHeader } from '../adminSettingsCommon';

const API = `${BACKEND_URL}/Api/render-server-jobs`;
const STATS_API = `${API}/stats`;
const PAGE_SIZE = 50;

const CATEGORY_LABEL = {
  'name-title': 'Naamtitels', 'narrowcasting': 'Narrowcasting',
  'srt': 'Ondertitels', 'qa': 'Video-QA', 'ritmo': 'Ritmo',
};
const CATEGORY_COLOR = {
  'name-title': { color: '#3b82f6', bg: '#dbeafe' },
  'narrowcasting': { color: '#7c3aed', bg: '#ede9fe' },
  'srt': { color: '#10b981', bg: '#ecfdf5' },
  'qa': { color: '#f59e0b', bg: '#fef3c7' },
  'ritmo': { color: '#ef4444', bg: '#fee2e2' },
};
const STATUS_LABEL = {
  pending: 'Wachtend', processing: 'Bezig', assigned: 'Toegewezen', rendering: 'Renderen',
  completed: 'Klaar', failed: 'Mislukt', error: 'Fout',
};
const STATUS_COLOR = {
  completed: { color: '#10b981', bg: '#ecfdf5' },
  failed: { color: '#ef4444', bg: '#fee2e2' }, error: { color: '#ef4444', bg: '#fee2e2' },
  pending: { color: '#f59e0b', bg: '#fef3c7' }, processing: { color: '#3b82f6', bg: '#dbeafe' },
  assigned: { color: '#3b82f6', bg: '#dbeafe' }, rendering: { color: '#3b82f6', bg: '#dbeafe' },
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1073741824) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
};
const fmtMs = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  return `${(n / 60000).toFixed(1)} min`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

function StatCard({ label, value, accent }) {
  return (
    <Box sx={{ bgcolor: T.surface, borderRadius: '0.6rem', p: 2, border: `1px solid ${T.surfaceHigh}` }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outline, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontFamily: T.font, fontSize: '1.4rem', fontWeight: 800, color: accent, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  );
}

export default function RenderServerJobs() {
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [filters, setFilters] = useState({
    job_category: urlParams.get('job_category') || '',
    status: 'all', user_email: '',
    worker_id: urlParams.get('worker_id') || '',
    from_date: '', to_date: '', search: '',
  });
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v && v !== 'all') params.set(k, v);
    return params;
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        authFetch(`${API}?${queryParams.toString()}&limit=${PAGE_SIZE}&offset=${(page - 1) * PAGE_SIZE}`),
        authFetch(`${STATS_API}?${queryParams.toString()}`),
      ]);
      if (!listRes.ok || !statsRes.ok) throw new Error();
      setRows(await listRes.json());
      setStats(await statsRes.json());
    } catch {
      toast.error('Render-server jobs laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [queryParams, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setF = (k) => (e) => { setPage(1); setFilters(prev => ({ ...prev, [k]: e.target.value })); };
  const reset = () => {
    setFilters({ job_category: '', status: 'all', user_email: '', worker_id: '', from_date: '', to_date: '', search: '' });
    setPage(1);
  };

  const COLS = '150px 120px 1fr 1fr 130px 80px 90px';

  return (
    <Box>
      <SectionHeader
        title="Render-server jobs"
        subtitle="Alle jobs die de render-server gebruiken — naamtitels, narrowcasting, ondertitels, video-QA en Ritmo — in één filterbaar overzicht."
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5, mb: 2 }}>
        <StatCard label="Jobs" value={stats?.total ?? 0} accent={T.primary} />
        <StatCard label="Succes" value={fmtPct(stats?.success_rate)} accent={T.emerald} />
        <StatCard label="Mislukt" value={stats?.failed ?? 0} accent="#ef4444" />
        <StatCard label="Avg render-tijd" value={fmtMs(stats?.avg_duration_ms)} accent={T.tertiary} />
      </Box>

      {/* Per-category breakdown chips */}
      {stats?.by_category?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {stats.by_category.map(c => {
            const col = CATEGORY_COLOR[c.key] || { color: T.outline, bg: T.surfaceLow };
            return (
              <Chip key={c.key}
                onClick={() => { setPage(1); setFilters(prev => ({ ...prev, job_category: prev.job_category === c.key ? '' : c.key })); }}
                label={`${CATEGORY_LABEL[c.key] || c.key}: ${c.count}`}
                size="small"
                sx={{
                  bgcolor: filters.job_category === c.key ? col.color : col.bg,
                  color: filters.job_category === c.key ? '#fff' : col.color,
                  fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ display: 'grid', gap: 1.5, mb: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr) auto auto' }, alignItems: 'center' }}>
        <TextField size="small" select label="Job-type" value={filters.job_category} onChange={setF('job_category')}>
          <MenuItem value="">Alles</MenuItem>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Status" value={filters.status} onChange={setF('status')}>
          <MenuItem value="all">Alles</MenuItem>
          <MenuItem value="completed">Klaar</MenuItem>
          <MenuItem value="failed">Mislukt</MenuItem>
          <MenuItem value="pending">Wachtend</MenuItem>
        </TextField>
        <TextField size="small" label="Gebruiker (email)" value={filters.user_email} onChange={setF('user_email')} />
        <TextField size="small" label="Render machine" value={filters.worker_id} onChange={setF('worker_id')} placeholder="hostname" />
        <TextField size="small" type="date" label="Van" InputLabelProps={{ shrink: true }} value={filters.from_date} onChange={setF('from_date')} />
        <TextField size="small" type="date" label="Tot" InputLabelProps={{ shrink: true }} value={filters.to_date} onChange={setF('to_date')} />
        <TextField size="small" label="Zoek (naam, bestand, email)" value={filters.search} onChange={setF('search')} sx={{ gridColumn: { md: 'span 4' } }} />
        <Button onClick={reset} startIcon={<FilterIcon />} size="small" sx={{ textTransform: 'none' }}>Reset</Button>
        <Button onClick={fetchData} startIcon={<RefreshIcon />} size="small" sx={{ textTransform: 'none' }}>Vernieuwen</Button>
      </Box>

      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline, mb: 1 }}>
        {loading ? 'Laden…' : `${rows.length} resultaten ${stats ? `(totaal ${stats.total})` : ''}`}
      </Typography>

      {/* Table */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}`, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 1, px: 2, py: 1.25,
          bgcolor: T.surfaceLow, fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outline,
        }}>
          <Box>Tijd</Box><Box>Type</Box><Box>Job</Box><Box>Gebruiker</Box><Box>Machine</Box><Box>Duur</Box><Box>Status</Box>
        </Box>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} sx={{ color: T.outlineVariant }} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>Geen jobs gevonden voor deze filters.</Typography>
          </Box>
        ) : rows.map(r => {
          const cat = CATEGORY_COLOR[r.job_category] || { color: T.outline, bg: T.surfaceLow };
          const st = STATUS_COLOR[r.status] || { color: T.outline, bg: T.surfaceLow };
          return (
            <Box key={`${r.source}-${r.id}`} onClick={() => setDetail(r)} sx={{
              display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 1, px: 2, py: 1.25,
              cursor: 'pointer', borderTop: `1px solid ${T.surfaceHigh}`, fontFamily: T.fontBody,
              fontSize: '0.78rem', color: T.text, '&:hover': { bgcolor: T.surfaceLow },
            }}>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: T.outline }}>{fmtDate(r.created_at)}</Box>
              <Box><Chip label={CATEGORY_LABEL[r.job_category] || r.job_category} size="small" sx={{ bgcolor: cat.bg, color: cat.color, fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, height: 22 }} /></Box>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label || '—'}</Box>
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Box sx={{ fontWeight: 600 }}>{r.user_name || '—'}</Box>
                <Box sx={{ fontSize: '0.7rem', color: T.outline }}>{r.user_email || '—'}</Box>
              </Box>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: T.outline, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.worker_id || '—'}</Box>
              <Box>{fmtMs(r.render_duration_ms)}</Box>
              <Box><Chip label={STATUS_LABEL[r.status] || r.status} size="small" sx={{ bgcolor: st.bg, color: st.color, fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, height: 22 }} /></Box>
            </Box>
          );
        })}
      </Box>

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!detail} onClose={() => setDetail(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}>
        {detail && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>Job details</Typography>
              <IconButton size="small" onClick={() => setDetail(null)}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ borderTop: `1px solid ${T.surfaceHigh}`, pt: 1.5 }}>
              {[
                ['Job-type', CATEGORY_LABEL[detail.job_category] || detail.job_category],
                ['Job ID', detail.job_id],
                ['Status', STATUS_LABEL[detail.status] || detail.status],
                ['Job', detail.label],
                ['Gebruiker', `${detail.user_name || '—'} (${detail.user_email || '—'})`],
                ['Render machine', detail.worker_id || '—'],
                ['Output', detail.output_filename || '—'],
                ['Bestandsgrootte', fmtBytes(detail.file_size)],
                ['Wachttijd', fmtMs(detail.queue_duration_ms)],
                ['Render-tijd', fmtMs(detail.render_duration_ms)],
                ['Aangemaakt', fmtDate(detail.created_at)],
                ['Voltooid', fmtDate(detail.completed_at)],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', gap: 2, py: 0.75 }}>
                  <Box sx={{ flex: '0 0 130px', fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outline, pt: 0.25 }}>{label}</Box>
                  <Box sx={{ flex: 1, fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, wordBreak: 'break-word' }}>{value ?? '—'}</Box>
                </Box>
              ))}
              {detail.error && (
                <Box component="pre" sx={{ mt: 1.5, fontFamily: 'monospace', fontSize: '0.72rem', color: '#991b1b', bgcolor: '#fef2f2', p: 1.5, borderRadius: '0.4rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{detail.error}</Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {stats && stats.total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={Math.ceil(stats.total / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} shape="rounded" />
        </Box>
      )}
    </Box>
  );
}
